import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Copy, Pencil } from "lucide-react";
import api, { apiErr, money } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, AdminHeading, Th, Td, adminInput } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

function EditExp({ exp, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(exp || { title: "", category: "", short_desc: "", description: "", base_price: 0, deposit: 0, capacity: 2, unit: "night", active: true, invitation_only: false });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      if (exp) await api.put(`/admin/experiences/${exp.id}`, f);
      else await api.post("/admin/experiences", f);
      toast.success("Saved."); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{exp ? <button className="text-muted-foreground hover:text-pool"><Pencil size={15} /></button> : <button className="btn-gold px-5 py-2.5 text-sm flex items-center gap-2" data-testid="add-exp-btn"><Plus size={15} /> New Experience</button>}</DialogTrigger>
      <DialogContent className="bg-surface border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{exp ? "Edit" : "New"} Experience</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input className={adminInput} placeholder="Title" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="exp-title" />
          <input className={adminInput} placeholder="Category" value={f.category || ""} onChange={(e) => set("category", e.target.value)} />
          <input className={adminInput} placeholder="Short description" value={f.short_desc || ""} onChange={(e) => set("short_desc", e.target.value)} />
          <textarea className={adminInput} rows={3} placeholder="Description" value={f.description || ""} onChange={(e) => set("description", e.target.value)} />
          <input className={adminInput} placeholder="Image URL" value={f.image || ""} onChange={(e) => set("image", e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" className={adminInput} placeholder="Base price" value={f.base_price} onChange={(e) => set("base_price", +e.target.value)} />
            <input type="number" className={adminInput} placeholder="Deposit" value={f.deposit} onChange={(e) => set("deposit", +e.target.value)} />
            <input type="number" className={adminInput} placeholder="Capacity" value={f.capacity} onChange={(e) => set("capacity", +e.target.value)} />
            <select className={adminInput} value={f.unit} onChange={(e) => set("unit", e.target.value)}><option value="night">Per night</option><option value="day">Per day</option><option value="hour">Per hour</option><option value="event">Per event</option></select>
          </div>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="accent-gold" /> Active</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.invitation_only} onChange={(e) => set("invitation_only", e.target.checked)} className="accent-gold" /> Invitation only</label>
          </div>
          <button onClick={save} className="btn-gold w-full py-2 text-sm" data-testid="exp-save">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminExperiences() {
  const [tab, setTab] = useState("experiences");
  const [exps, setExps] = useState(null);
  const [addons, setAddons] = useState(null);
  const load = () => { api.get("/admin/experiences").then((r) => setExps(r.data)).catch(() => setExps([])); api.get("/admin/addons").then((r) => setAddons(r.data)).catch(() => setAddons([])); };
  useEffect(() => { load(); }, []);
  const clone = async (id) => { try { await api.post(`/admin/experiences/${id}/clone`); toast.success("Cloned."); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleAddon = async (a) => { try { await api.put(`/admin/addons/${a.id}`, { active: !a.active }); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div>
      <AdminHeading title="Experiences & Add-ons">{tab === "experiences" && <EditExp onDone={load} />}</AdminHeading>
      <div className="flex gap-2 mb-4">
        {["experiences", "addons"].map((t) => <button key={t} onClick={() => setTab(t)} className={`px-4 py-1.5 rounded-full text-sm capitalize ${tab === t ? "bg-pool/15 text-pool border border-pool/40" : "text-muted-foreground border border-white/10"}`}>{t}</button>)}
      </div>
      {tab === "experiences" ? (
        exps === null ? <Loading /> : (
          <Panel><div className="overflow-x-auto"><table className="w-full min-w-[600px]">
            <thead><tr><Th>Title</Th><Th>Category</Th><Th>Price</Th><Th>Active</Th><Th></Th></tr></thead>
            <tbody>{exps.map((e) => (
              <tr key={e.id}><Td>{e.title}</Td><Td className="text-muted-foreground">{e.category}</Td><Td>{e.invitation_only ? "By Invitation" : money(e.base_price)}</Td><Td>{e.active ? "Yes" : "No"}</Td>
                <Td><div className="flex gap-3 justify-end"><EditExp exp={e} onDone={load} /><button onClick={() => clone(e.id)} className="text-muted-foreground hover:text-gold" title="Clone"><Copy size={15} /></button></div></Td>
              </tr>
            ))}</tbody>
          </table></div></Panel>
        )
      ) : (
        addons === null ? <Loading /> : (
          <Panel><div className="overflow-x-auto"><table className="w-full min-w-[500px]">
            <thead><tr><Th>Name</Th><Th>Type</Th><Th>Price</Th><Th>Active</Th></tr></thead>
            <tbody>{addons.map((a) => (
              <tr key={a.id}><Td>{a.name}</Td><Td className="text-muted-foreground text-xs">{a.pricing_type} · {a.charge_type}</Td><Td>{money(a.price)}</Td>
                <Td><button onClick={() => toggleAddon(a)} className={`text-xs px-2 py-1 rounded-full ${a.active ? "bg-pool/15 text-pool" : "bg-white/5 text-muted-foreground"}`}>{a.active ? "Active" : "Inactive"}</button></Td>
              </tr>
            ))}</tbody>
          </table></div></Panel>
        )
      )}
    </div>
  );
}
